import { Button, Card, SvgIcon, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { RiAddLine, RiAlertFill } from '@remixicon/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { MainSectionTemplate } from '../../../components/AdminTemplates';
import CustomDataGrid from '../../../components/CustomDataGrid';
import VesselOrCrewStatusBadge, {
  ExpiringSoonBadge,
} from '../../../components/VesselOrCrewStatusBadge';
import { useAppDataContext } from '../../../contexts/appData';
import { useCurrentUser } from '../../../contexts/currentUser';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import {
  MINIMUM_AMOUNT_OF_VESSELS_PER_OPERATOR,
  VesselListItem,
} from '../../../types/vessel';
import AdminTableDetailLink from '../../../components/AdminTableDetailLink';
import { VesselStatus } from '../../../types/core';
import { useDataGridSearchParamsFiltersManager } from '../../../hooks/useSearchParamsFilters';

export const VesselsPage = () => {
  const { t } = useLanguage();
  const { isOperator } = useCurrentUser();
  const { getVessels } = useIotsApi();
  const { appData } = useAppDataContext();

  const {
    data: vessels = [],
    isFetching,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['vessels'],
    queryFn: () => getVessels(),
    // Default sorting of vessels for the list based on the first 2 columns
    select: (vessels: VesselListItem[]) =>
      [...vessels]
        .filter(
          /* Only non deleted vessels will be displayed */
          ({ deleteDate }) => !deleteDate,
        )
        .sort(
          (a, b) =>
            a.operatorName.localeCompare(b.operatorName) ||
            a.vesselName.localeCompare(b.vesselName),
        ),
  });

  useQueryErrorNotifier([error]);

  const { initialFilterModel, onFilterModelChange } =
    useDataGridSearchParamsFiltersManager();

  const columns = useMemo<GridColDef<VesselListItem>[]>(
    () => [
      {
        field: 'vesselName',
        flex: 3,
        headerName: t('content.vessel-name'),
        minWidth: 200,
      },
      ...(!isOperator
        ? [
            {
              field: 'operatorName',
              flex: 3,
              headerName: t('content.operator'),
              minWidth: 220,
            },
          ]
        : []),
      {
        field: 'regNumber',
        flex: 2,
        headerName: t('content.reg-or-imo'),
        minWidth: 160,
      },
      {
        type: 'number',
        field: 'pendingCertificationsCount',
        headerName: t('content.pending-certifications'),
        flex: 2,
        cellClassName: params => (params.value > 0 ? 'bg-iots-red-500/10' : ''),
        minWidth: 160,
      },
      {
        type: 'number',
        field: 'validCertificationsCount',
        headerName: t('content.valid-certifications'),
        display: 'flex',
        cellClassName: params => (!params.value ? 'bg-iots-red-500/10' : ''),
        renderCell: params => (
          <Tooltip
            key={`vessels-tooltip-${params.id}`}
            title={t('validation.valid-certifications-minimum')}
            disableHoverListener={Boolean(params.value)}
          >
            <div>{params.value}</div>
          </Tooltip>
        ),
      },
      {
        field: 'status',
        display: 'flex',
        minWidth: 184,
        headerName: t('content.status'),
        type: 'singleSelect',
        valueOptions: appData.vesselStatus.map(status => ({
          value: status,
          label: t(`vessel-status.${status}`),
        })),
        renderCell: params => (
          <VesselOrCrewStatusBadge
            className="overflow-visible"
            status={params.value as VesselStatus}
            entity="vessel"
            small
          />
        ),
      },
      {
        type: 'singleSelect',
        field: 'expiredSoon',
        headerName: t('content.expiring-soon'),
        renderHeader: () => <ExpiringSoonBadge inheritColor entity="vessel" />,
        display: 'flex',
        valueOptions: [
          {
            value: true,
            label: t(`content.expiring-soon`),
          },
          {
            value: false,
            label: t(`content.not-expiring-soon`),
          },
        ],
        renderCell: params =>
          params.row.expiredSoon && <ExpiringSoonBadge entity="vessel" />,
      },
      {
        field: '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        hideable: false,
        display: 'flex',
        renderCell: params => (
          <AdminTableDetailLink
            isLoading={isFetching}
            href={`/admin/vessels/${params.id}`}
          />
        ),
      },
    ],
    [isOperator, isFetching, t, appData],
  );

  return (
    <MainSectionTemplate
      section={t('content.fleet')}
      title={t('content.vessels')}
      isLoading={isFetching}
      actions={
        <div className="flex flex-col items-center gap-2">
          {!isLoading &&
            vessels.length < MINIMUM_AMOUNT_OF_VESSELS_PER_OPERATOR && (
              <div className="flex items-center">
                <RiAlertFill className="w-4 h-4 mr-1 text-yellow-600" />
                {t('content.minimum-of-2')}
              </div>
            )}
          <Link to="/admin/vessels/create">
            <Button
              variant="contained"
              startIcon={<SvgIcon component={RiAddLine} />}
              size="small"
            >
              {t('content.add-new')}
            </Button>
          </Link>
        </div>
      }
    >
      <Card variant="outlined">
        {!vessels.length && !isFetching ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <h1 className="text-4xl font-bold">
                {t('content.no-vessels-found-title')}
              </h1>
              <h2 className="text-base mt-2 opacity-50">
                {t('content.no-vessels-found-subtitle')}
              </h2>
            </div>
          </div>
        ) : (
          <CustomDataGrid
            rows={vessels}
            loading={isLoading}
            columns={columns}
            onFilterModelChange={onFilterModelChange}
            initialState={{
              filter: {
                filterModel: initialFilterModel,
              },
              sorting: {
                sortModel: [
                  {
                    field: 'vesselName',
                    sort: 'asc',
                  },
                ],
              },
            }}
          />
        )}
      </Card>
    </MainSectionTemplate>
  );
};

export default VesselsPage;
